import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';

import html2canvas from 'html2canvas';
import ButtonSimple from './ButtonSimple';
import { IconAiOutlineDownload } from '@/utils/icons';
import TooltipCustom from './TooltipCustom';

interface TableSchema {
  type: 'table';
  headers: string[];
  data: Record<string, any>[];
  footer?: string;
  title?: string;
  description?: string;
}

interface DynamicTableProps {
  schema: TableSchema;
  size?: 'small' | 'middle' | 'large';
  bordered?: boolean;
  showHeader?: boolean;
  pagination?: boolean | object;
  scroll?: { x?: number; y?: number };
  className?: string;
  loading?: boolean;
  summary?: boolean;
  chartName?: string;
}





const DynamicTable: React.FC<DynamicTableProps> = ({
  schema,
  size = 'small',
  bordered = true,
  showHeader = true,
  pagination = false,
  scroll,
  className = '',
  loading = false,
  summary = true,
  chartName = ''
}) => {

  const tableRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState<number | undefined>(undefined);
  const [scrollX, setScrollX] = useState<number | string>(500);

  const columns: ColumnsType<Record<string, any>> = useMemo(() => {

    return schema.headers.map((header, index) => {
      const dataIndex = header;

      return {
        title: header,
        dataIndex,
        key: `col-${index}`,
        align: 'left' as const,
        width: `${100 / schema.headers.length}%`,
        className: 'text-[10px] !text-center !p-[4px] ',
        tooltip: true,
        render: (value: any, record: Record<string, any>, recordIndex: number) => {

          // Check if this is a total/summary row
          const isTotal = schema.footer && (String(value).toLowerCase().includes('total') || String(record[schema.headers[0] || '']).toLowerCase().includes('total'));

          // Special formatting for numeric values
          if (typeof value === 'number') {
            return (
              <span className={isTotal ? 'font-semibold text-gray-800' : ''} key={recordIndex}>
                {value == 0 ? '' : value}
                {/* {value.toLocaleString()} */}
              </span>
            );
          }

          return (
            <span className={isTotal ? 'font-semibold text-gray-800' : ''} key={recordIndex}>
              {value == '0' || value == 0 ? '' : value}
            </span>
          );
        },
        sorter: (a: Record<string, any>, b: Record<string, any>) => {
          const aVal = a[dataIndex];
          const bVal = b[dataIndex];

          // Don't sort total rows
          if (schema.footer &&
            (String(aVal).toLowerCase().includes('total') ||
              String(bVal).toLowerCase().includes('total'))) {
            return 0;
          }

          // Sort numbers
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return aVal - bVal;
          }

          // Sort strings
          return String(aVal).localeCompare(String(bVal));
        }
      };

    });
  }, [schema.headers, schema.footer]);

  const dataWithKeys = useMemo(() => {
    return schema.data.map((row, index) => ({
      ...row,
      key: `row-${index}`
    }));
  }, [schema.data]);

  useEffect(() => {
    if (tableRef.current) {
      const tableBody = tableRef.current.querySelector('.custom-dynamic-table');
      if (tableBody) {
        const bodyHeight = tableBody.scrollHeight;
        setScrollY(bodyHeight > 500 ? 500 : undefined);
      }
    }
  }, [dataWithKeys]);

  const exportAsPNG = async () => {
    if (tableRef.current) {
      setScrollX('max-content');
      await new Promise(resolve => setTimeout(resolve, 500));
      const canvas = await html2canvas(tableRef.current, { scale: 1 });
      const link = document.createElement('a');
      link.download = `${chartName || 'table'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      setScrollX(500);
    }
  };

  const summaryData = useMemo(() => {
    if (!summary || !schema.data.length) {
      return null;
    }

    const numericColumns = schema.headers.filter(header => {
      return schema.data.some(row => typeof row[header] === 'number');
    });

    if (numericColumns.length === 0) {
      return null;
    }

    const totals: Record<string, any> = {};

    totals[schema.headers[0] || ''] = 'Total';

    numericColumns.forEach(header => {
      const sum = schema.data
        .filter(row => {
          return !String(row[schema.headers[0] || '']).toLowerCase().includes('total');
        })
        .reduce((acc, row) => {
          const value = row[header];
          return acc + (typeof value === 'number' ? value : 0);
        }, 0);
      totals[header] = sum;
    });

    return totals;
  }, [schema.data, schema.headers, summary]);

  const getRowClassName = (record: Record<string, any>, index: number) => {

    const isTotal = schema.footer && String(record[schema.headers[0] || '']).toLowerCase().includes('total');

    if (isTotal) {
      return 'bg-gray-50 border-t-2 border-gray-300';
    }

    return index % 2 === 0 ? 'bg-white' : 'bg-gray-25';
  };



  return (
    <div>

      <TooltipCustom title="Export as PNG">
        <ButtonSimple
          onClick={exportAsPNG}
          className='flex justify-self-end mb-1'
        >
          <IconAiOutlineDownload />
        </ButtonSimple>
      </TooltipCustom>

      <div ref={tableRef} >
        <p className=" text-xl font-semibold leading-none tracking-tight base-card-title !text-center w-full mb-3 ">{chartName}</p>
        <div
          className="dynamic-table-container"
          id="capture-me"
        >
          <Table
            columns={columns}
            dataSource={dataWithKeys}
            size="small"
            bordered={bordered}
            showHeader={showHeader}
            pagination={false}
            // scroll={{ y: 500 }}
            // scroll={scrollY ? { x: 'max-content' } : { x: 'max-content' }}
            // scroll={scrollY ? { y: scrollY, x: scrollX } : { x: scrollX }}
            sticky={true}
            loading={loading}
            className={`custom-dynamic-table ${className}`}
            rowClassName={getRowClassName}
            summary={summary && summaryData ? () => (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-blue-50 font-semibold">
                  {schema.headers.map((header, index) => (
                    <Table.Summary.Cell key={`summary-${index}`} index={index}>
                      <strong>
                        {summaryData[header] !== undefined
                          ? typeof summaryData[header] === 'number'
                            ? summaryData[header].toLocaleString()
                            : summaryData[header]
                          : ''
                        }
                      </strong>
                    </Table.Summary.Cell>
                  ))}
                </Table.Summary.Row>
              </Table.Summary>
            ) : undefined}
          />
        </div>
      </div>

    </div>
  );
};

export default DynamicTable;
